import { NetworkStatus, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Box, ToastType } from '@bechtel/gds';
import {
  Cable,
  CableTermType,
  CableView,
  Commodity,
  MetadataTableName,
  OperatorType,
  PackageCommodity,
  PackageCommodityType,
  SaveCommodityPackagesResult,
  SavePackageCommoditiesInput,
  UiMessageKey,
  UiTextEnum,
  UserAccountLayout,
} from '@bechtel/sr-types';
import { cloneDeep, filter, find, get, includes, isEmpty, isEqual, map } from 'lodash';
import { FC, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { v5 as uuidv5 } from 'uuid';
import { CommodityFooter, ErrorRetry, GridSkeleton, useAppUser, useLanguage } from '~/components';
import {
  GET_PACKAGE_BY_ID,
  GET_PACKAGE_COMMODITIES,
  SAVE_PACKAGE_COMMODITIES,
  SEARCH_CABLE,
  SEARCH_EQUIPMENT,
  SEARCH_JUMPERS,
  SEARCH_RACEWAY,
} from '~/gql';
import { usePermissions, useSelectListQuery, useTableMetadataQuery, useToast } from '~/hooks';
import { clearPackageCache } from '~/utilities';
import { ClientGrid } from '../grid';
import { PackageCommoditiesProps } from './types';
const MessageModal = lazy(() => import('~/components/organisms/message-modal/message'));

const getCommoditySearchQuery = (commodityType: PackageCommodityType) => {
  switch (commodityType) {
    case PackageCommodityType.Jumper:
      return SEARCH_JUMPERS;
    case PackageCommodityType.Raceway:
      return SEARCH_RACEWAY;
    case PackageCommodityType.Equipment:
      return SEARCH_EQUIPMENT;
    case PackageCommodityType.Cable:
      return SEARCH_CABLE;
    case PackageCommodityType.CableTerm:
      return SEARCH_CABLE;
    default:
      return SEARCH_CABLE;
  }
};

const getCommodityReturnObject = (commodityType: PackageCommodityType) => {
  switch (commodityType) {
    case PackageCommodityType.Jumper:
      return 'searchJumpers.items';
    case PackageCommodityType.Raceway:
      return 'searchRaceways.items';
    case PackageCommodityType.Equipment:
      return 'searchEquipments.items';
    case PackageCommodityType.Cable:
      return 'searchCables.items';
    case PackageCommodityType.CableTerm:
      return 'searchCables.items';
    default:
      return 'searchCables.items';
  }
};

const getEquipmentTag = (commType: CableTermType, cableView?: CableView | null) => {
  if (commType === CableTermType.F) return cableView?.fromEquipmentTag;
  if (commType === CableTermType.T) return cableView?.toEquipmentTag;
};

export const PackageCommodities: FC<PackageCommoditiesProps> = ({
  tableName,
  systemLayoutName,
  packageId,
  commodityType,
}) => {
  const [packageCommodities, setPackageCommodities] = useState<PackageCommodity[]>([]);
  const [initialPackageCommodities, setInitialPackageCommodities] = useState<PackageCommodity[]>([]);
  const { showToast } = useToast();
  const [searchCommodities] = useLazyQuery(getCommoditySearchQuery(commodityType));
  const searchCommoditiesRef = useRef<any>(null);
  const { userAccount } = useAppUser();
  const { canSetPackage } = usePermissions();
  const { id } = useParams();
  const { getMultiLingualTextByKey } = useLanguage();
  const entityText = getMultiLingualTextByKey('Package');

  const [getSelectListData, { data: selectListData }] = useSelectListQuery('PACKAGE_TERM.COMM_TYPE');

  useEffect(() => {
    getSelectListData();
  }, [getSelectListData]);

  const [getPackageTermTableMetaData, { data: packageTermMetaData }] = useTableMetadataQuery();
  const [getEquipmentTableMetaData, { data: equipmentMetaData }] = useTableMetadataQuery();

  // Get package term and equipment metadata
  useEffect(() => {
    if (!userAccount?.defaultProject?.id || tableName !== MetadataTableName.PACKAGE_TERM) return;
    getPackageTermTableMetaData({
      variables: {
        name: MetadataTableName.PACKAGE_TERM,
        projectId: userAccount?.defaultProject?.id,
      },
    });
    getEquipmentTableMetaData({
      variables: {
        name: MetadataTableName.EQUIPMENT,
        projectId: userAccount?.defaultProject?.id,
      },
    });
  }, [userAccount, getPackageTermTableMetaData, tableName, getEquipmentTableMetaData]);

  const { loading, error, refetch, networkStatus } = useQuery<{
    commoditiesByPackage: [PackageCommodity];
  }>(GET_PACKAGE_COMMODITIES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      packageId: packageId,
      packageCommodityType: commodityType,
    },
    onCompleted: (data) => {
      if (data.commoditiesByPackage?.length) {
        setPackageCommodities(sanitizeRow(data.commoditiesByPackage));
        setInitialPackageCommodities(sanitizeRow(data.commoditiesByPackage));
      }
    },
  });

  const sanitizeRow = useCallback((commoditiesByPackage: PackageCommodity[]) => {
    const sanitizedCommoditiesByPackage = map(commoditiesByPackage, (item) => {
      return {
        ...item,
        equipmentTag: getEquipmentTag(item.commType as CableTermType, item.cableView),
      };
    });
    return sanitizedCommoditiesByPackage;
  }, []);

  const onEdited = useCallback(
    (prevPackageCommodities, editedRow) => {
      //tag can be picked from picklist or grid column or on datepicker change
      const tag = editedRow['commodity.tag'] || editedRow?.commodity?.tag;
      const index = prevPackageCommodities?.findIndex((item) => item?.id === editedRow.id);

      const commodityObj: Commodity =
        searchCommoditiesRef.current && searchCommoditiesRef.current.length > 0
          ? searchCommoditiesRef.current[0]
          : editedRow.commodity;
      const newPackageCommodity: PackageCommodity = {
        ...editedRow,
        id: editedRow.id ?? crypto.randomUUID(),
        commodity: { ...commodityObj },
        commodityId: commodityObj?.id,
        packageId: packageId,
        projectId: commodityObj?.projectId ?? userAccount?.defaultProjectId,
        updatedTime: new Date(),
        updatedBy: '',
      };
      //cableView is required only for Package Term to get equipment tag
      if (tableName === MetadataTableName.PACKAGE_TERM) {
        const commodity = newPackageCommodity.commodity as Cable;
        //override newPackageCommodity cableView with commodity cableView
        newPackageCommodity.cableView = commodity.cableView ?? newPackageCommodity.cableView;
      }
      if (editedRow.dateChanged) {
        const packageCommodities = [...prevPackageCommodities];
        const updatedIndex = index === -1 ? prevPackageCommodities.length : index;
        packageCommodities[updatedIndex] = { ...editedRow };
        return [...sanitizeRow(packageCommodities)];
      }
      if (tag) {
        const updatedIndex = index === -1 ? prevPackageCommodities.length : index;
        const packageCommodities = [...prevPackageCommodities];
        packageCommodities[updatedIndex] = { ...newPackageCommodity };
        return [...sanitizeRow(packageCommodities)];
      }
      return [...prevPackageCommodities];
    },
    [packageId, sanitizeRow, tableName, userAccount?.defaultProjectId]
  );

  const onPackageCommoditiesEdited = useCallback(
    (editedRow: PackageCommodity) => {
      setPackageCommodities((prev) => onEdited(prev, editedRow));
    },
    [onEdited, setPackageCommodities]
  );

  const [savePackageCommodities, { data: savePackageCommoditiesData, loading: savePackageCommoditiesLoading }] =
    useMutation<
      { savePackageCommodities: SaveCommodityPackagesResult },
      { savePackageCommoditiesInput: SavePackageCommoditiesInput }
    >(SAVE_PACKAGE_COMMODITIES, {
      onCompleted: (data) => {
        if (data) {
          const updatedPackageCommodities = get(data, 'savePackageCommodities.commodityPackages');
          if (updatedPackageCommodities) {
            showToast(UiTextEnum.SAVED_SUCCESSFULLY_TOAST, [entityText, ''], ToastType.INFO);
          }
        }
      },
      onError: () => {
        showToast(UiTextEnum.ERROR_WHILE_UPDATING_TOAST, ['Package Commodities', ''], ToastType.INFO);
      },
      update: (cache, { data }) => {
        const updatedPackageCommodities = get(data, 'savePackageCommodities.commodityPackages');
        if (updatedPackageCommodities) {
          cache.writeQuery({
            query: GET_PACKAGE_COMMODITIES,
            data: { commoditiesByPackage: updatedPackageCommodities },
            variables: {
              packageId: packageId,
              packageCommodityType: commodityType,
            },
          });
        }
        clearPackageCache(cache);
      },
      refetchQueries: [
        {
          query: GET_PACKAGE_BY_ID,
          variables: {
            id: id,
          },
        },
      ],
    });

  const onSavePackageCommodities = useCallback(() => {
    savePackageCommodities({
      variables: {
        savePackageCommoditiesInput: {
          packageCommodities: map(packageCommodities, (item) => {
            return {
              commodityId: item.commodity?.id || '',
              assignedDate: item.assignedDate,
              asBuiltDate: item.asBuiltDate,
              commType: item.commType,
            };
          }),
          packageCommodityType: commodityType,
          packageId: packageId,
        },
      },
    });
  }, [savePackageCommodities, packageCommodities, commodityType, packageId]);

  const errorKeys: UiMessageKey[] = get(savePackageCommoditiesData, 'savePackageCommodities.errors') ?? [];

  const onDeleted = useCallback((deletedIDs: string[]) => {
    setPackageCommodities((prev) => filter(prev, (item) => !includes(deletedIDs, item.id)));
  }, []);

  const validateTag = useCallback(
    async (value, isPicked) => {
      const existingRow = find(packageCommodities, (item) => item?.commodity?.tag === value);
      searchCommoditiesRef.current = null;
      //for non term items, we don't need to search commodity as equipment(T/F) is not required
      if (isPicked && tableName !== MetadataTableName.PACKAGE_TERM) {
        return isEmpty(existingRow);
      } else {
        const { data } = await searchCommodities({
          variables: {
            searchInput: {
              filterInput: {
                and: {
                  field: 'tag',
                  operator: OperatorType.EQUAL,
                  value: value,
                },
              },
            },
          },
        });
        const result = get(data, getCommodityReturnObject(commodityType));
        searchCommoditiesRef.current = result;
        return result.length > 0 && isEmpty(existingRow);
      }
    },
    [commodityType, packageCommodities, searchCommodities, tableName]
  );

  const validationFns = useMemo(() => {
    return {
      commodityId: async (value, api, isPicked) => {
        return validateTag(value, isPicked);
      },
      commType: async (value) => {
        const selectListResult = get(selectListData, 'getSelectListData.items');
        const exists = selectListResult.some((item) => item.column1 === value);
        return exists;
      },
    };
  }, [selectListData, validateTag]);

  const customTableMetaData = useMemo(() => {
    if (!packageTermMetaData || !equipmentMetaData) return null;
    // Clone and combine columns for package term and equipment tag
    const clonedTableMetadata = cloneDeep(packageTermMetaData);
    const equipmentTagColumn = equipmentMetaData.tableMetadata.metadataColumns.find(
      (column) => column.memberName === 'tag'
    );

    if (equipmentTagColumn) {
      const updatedEquipmentTagColumn = {
        ...equipmentTagColumn,
        memberName: 'equipmentTag',
        isUserEditable: false,
        requiredData: 0,
        pickListName: null,
      };

      clonedTableMetadata.tableMetadata.metadataColumns = [
        ...packageTermMetaData.tableMetadata.metadataColumns,
        updatedEquipmentTagColumn,
      ];
    }

    // form custom layout which is applicable to all the package terms
    const packageTermSystemLayout: UserAccountLayout = {
      id: uuidv5('CUSTOM_SYSTEM_PACKAGE_TERM', userAccount?.defaultProject?.id || ''),
      userAccountId: userAccount?.id || '',
      layoutName: 'custom.packageTerm',
      description: 'CUSTOM_SYSTEM_PACKAGE_TERM',
      projectId: userAccount?.defaultProject?.id || '',
      selectClause: 'id, commodityId, commType, equipmentTag, assignedDate, asBuiltDate',
      whereClause: null,
      orderByClause: null,
    };
    clonedTableMetadata.tableMetadata.systemAccountLayouts = [packageTermSystemLayout];
    return clonedTableMetadata;
  }, [equipmentMetaData, packageTermMetaData, userAccount?.defaultProject?.id, userAccount?.id]);

  if (loading || networkStatus === NetworkStatus.refetch) {
    return <GridSkeleton />;
  }

  if (error) {
    return <ErrorRetry refetch={refetch} />;
  }

  return (
    <>
      <Box flexGrow={{ sm: ' 1' }} flexDirection={{ sm: 'column' }}>
        <ClientGrid
          hideSearch
          hideSaveLayout
          showHeader
          rowSelection="multiple"
          getRowId={(data) => data.id}
          name={tableName === MetadataTableName.PACKAGE_TERM ? 'CUSTOM_METADATA' : tableName}
          systemLayoutName={tableName === MetadataTableName.PACKAGE_TERM ? 'custom.packageTerm' : systemLayoutName}
          rowData={packageCommodities}
          originalRowData={initialPackageCommodities}
          onRowEdited={onPackageCommoditiesEdited}
          onRowsDeleted={onDeleted}
          validationFns={validationFns}
          readOnly={!canSetPackage}
          picklistKeys={{ commodityId: { selectKey: 'tag', returnKey: 'commodity', expectsObj: true } }}
          tableMetaData={tableName === MetadataTableName.PACKAGE_TERM ? customTableMetaData : null}
          skipPermission={true}
        ></ClientGrid>
        <CommodityFooter
          uiType="Grid"
          saveHandler={onSavePackageCommodities}
          loading={savePackageCommoditiesLoading}
          disableSave={!canSetPackage || isEqual(packageCommodities, initialPackageCommodities)}
          disableCancel={isEqual(packageCommodities, initialPackageCommodities)}
          cancelHandler={() => {
            setPackageCommodities([...initialPackageCommodities]);
            searchCommoditiesRef.current = [];
          }}
        />
      </Box>
      {errorKeys && errorKeys.length > 0 && <MessageModal messageKeys={errorKeys}></MessageModal>}
    </>
  );
};
